import React, { useEffect, useRef, useState } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { LensDatasetDryRunResponse } from '~/hooks/apis/reporting/useLeLensDatasetChecks';

import type {
  LensCard,
  LensDataset,
  LensDatasetColumn,
  LensDatasetDateFilter,
  LensDatasetField,
  LensFilter,
} from '~/types/leLens';

import DatasetFieldsEditor from './DatasetFieldsEditor';
import DatasetHistoryDialog from './DatasetHistoryDialog';
import { dateColumnFromBinding } from './buildPlan';
import {
  type CombineDecision,
  type CombineOutcome,
  type KeptReason,
  describeKeptReason,
  describeQueryPosture,
} from './combineQueries';
import { prepareFields } from './datasetFields';
import { declaredDataset } from './panel/DeclareNow';

type DatasetsDialogProps = {
  open: boolean;
  lensId?: string;
  lensVersion?: number;
  canEdit?: boolean;
  datasets: Array<LensDataset>;
  cards: Array<LensCard>;
  /** Dashboard filters, so a shared query is written to bind the ones in use. */
  filters?: Array<LensFilter>;
  /**
   * Finds safe shared sources and reports only truthful progress. Absent on a
   * read-only surface, which hides the action.
   */
  onCombine?: (onStatus: (message: string) => void) => Promise<CombineOutcome>;
  /** Writes a reviewed shared-query proposal. Discovery stays separate so a
   * model-generated query never silently changes a dashboard. */
  onApplyCombination?: (outcome: CombineOutcome) => Promise<void>;
  onClose: () => void;
  /** Types a query against the warehouse without running it. A rewritten query cannot be saved
   *  until this has answered for the text as it stands. */
  onCheckQuery: (sql: string) => Promise<LensDatasetDryRunResponse>;
  onSave: (datasets: Array<LensDataset>) => Promise<void>;
  /** Removing a dataset also has to say what happens to the cards reading it. */
  onDelete: (datasetId: string) => Promise<void>;
  isSaving: boolean;
};

/** How a message is meant, so a failure never renders as a neutral note. */
type Notice = { kind: 'info' | 'warning' | 'error'; text: string };

/**
 * A reviewed proposal, stamped with the board it was computed from.
 *
 * Applying writes the whole card list the proposal carries, so a card added,
 * moved, renamed or re-queried after the proposal was prepared would be
 * overwritten by the version the proposal remembers. The stamp is how that is
 * caught: the proposal is only appliable to the board it was made for.
 */
type Proposal = CombineOutcome & { basis: string };

/** Everything applying a proposal would overwrite. */
function boardSignature(cards: ReadonlyArray<LensCard>): string {
  return JSON.stringify(cards);
}

type Source = {
  maxAgeSeconds?: number;
  deletedAt?: string;
  id: string;
  name: string;
  sql: string;
  /** The query already on the dashboard, so an untouched dataset is not made to re-prove itself. */
  savedSql: string;
  savedColumns: Array<LensDatasetColumn>;
  checked?: { sql: string; result: LensDatasetDryRunResponse };
  dateFilter?: LensDatasetDateFilter;
  fields?: Array<LensDatasetField>;
};

/** What the last check found, only while it still describes the text in the box. */
function checkedResult(entry: Source): LensDatasetDryRunResponse | undefined {
  return entry.checked?.sql === entry.sql.trim() ? entry.checked.result : undefined;
}

function isEdited(entry: Source): boolean {
  return entry.sql.trim() !== entry.savedSql;
}

function isUnchecked(entry: Source): boolean {
  return isEdited(entry) && !checkedResult(entry);
}

function columnsOf(entry: Source): Array<string> {
  const typed = checkedResult(entry)?.columns ?? (isEdited(entry) ? [] : entry.savedColumns);
  return typed.map((column) => column.name);
}

function describeColumns(entry: Source): string {
  if (isUnchecked(entry)) {
    return 'This query has changed. Check it before saving.';
  }
  const count = columnsOf(entry).length;
  return count ? `Returns ${count} ${count === 1 ? 'column' : 'columns'}.` : 'Columns not declared yet.';
}

/**
 * Every query on the dashboard in one place, since a dataset no card owns
 * would otherwise be written once and never seen again.
 */
export default function DatasetsDialog({
  open,
  lensId,
  lensVersion,
  canEdit,
  datasets,
  cards,
  onCombine,
  onApplyCombination,
  onClose,
  onCheckQuery,
  onSave,
  onDelete,
  isSaving,
}: DatasetsDialogProps) {
  const [historyDataset, setHistoryDataset] = useState<LensDataset | null>(null);
  const [draft, setDraft] = useState<Array<Source>>([]);
  // Bumped when the cards change under an open dialog, which combining does,
  // so the draft is re-read rather than left describing a board that no longer
  // exists.
  const [seed, setSeed] = useState(0);
  const [tab, setTab] = useState(0);
  const [isCombining, setIsCombining] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [pendingCombination, setPendingCombination] = useState<Proposal | null>(null);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [checkFailure, setCheckFailure] = useState<{ id: string; sql: string; text: string } | null>(null);
  // A combine runs several model turns and cannot be called back, so cancelling
  // has to be a decision here: a run the reader walked away from must not be
  // able to put a proposal on screen later, over a board they have since edited.
  const attempt = useRef(0);
  const readerCounts = new Map<string, number>();
  cards.forEach((card) => {
    if (card.datasetId) readerCounts.set(card.datasetId, (readerCounts.get(card.datasetId) ?? 0) + 1);
  });
  const combinableSources = cards.filter(
    (card) =>
      card.datasetId &&
      !datasets.find((dataset) => dataset.id === card.datasetId)?.deletedAt &&
      readerCounts.get(card.datasetId) === 1 &&
      Boolean(datasets.find((dataset) => dataset.id === card.datasetId)?.sql),
  ).length;
  const staleProposal = Boolean(pendingCombination) && pendingCombination?.basis !== boardSignature(cards);

  useEffect(() => {
    if (!open) {
      return;
    }
    setDraft(
      datasets.map((dataset) => ({
        id: dataset.id,
        deletedAt: dataset.deletedAt,
        name: dataset.name,
        sql: dataset.sql,
        savedSql: dataset.sql.trim(),
        maxAgeSeconds: dataset.maxAgeSeconds,
        savedColumns: dataset.columns ?? [],
        dateFilter: dataset.dateFilter,
        fields: dataset.fields,
      })),
    );
    setTab(0);
    setNotice(null);
    setPendingCombination(null);
    setCheckingId(null);
    setCheckFailure(null);
    // Seed only on the open transition. Keying this on datasets/cards would
    // re-seed on every render that hands in a referentially-new-but-equal
    // array (lens.spec.datasets ?? [] is a fresh literal on every render when
    // there are no shared datasets, and a background lens refetch does the
    // same for cards), discarding SQL a reader is mid-edit. A lens switch
    // replaces this whole tree with LensLoading while it loads, so reopening
    // always reads the lens actually on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seed]);

  const current = draft[tab];
  const readers = current ? cards.filter((card) => card.datasetId === current.id) : [];
  const patchSource = (id: string, patch: Partial<Source>) =>
    setDraft((entries) => entries.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
  const update = (patch: Partial<Source>) => {
    if (current) patchSource(current.id, patch);
  };

  const checkCurrentQuery = async () => {
    if (!current) return;
    const { id } = current;
    const sql = current.sql.trim();
    setCheckFailure(null);
    setCheckingId(id);
    try {
      patchSource(id, { checked: { sql, result: await onCheckQuery(sql) } });
    } catch (error) {
      setCheckFailure({
        id,
        sql,
        text: error instanceof Error ? error.message : 'Could not check this query just now.',
      });
    } finally {
      setCheckingId(null);
    }
  };

  const findSharedQueries = async () => {
    if (!onCombine) return;
    const run = attempt.current + 1;
    attempt.current = run;
    const basis = boardSignature(cards);
    setIsCombining(true);
    setNotice({ kind: 'info', text: 'Checking whether cards can share a query. Nothing is changed yet.' });
    setPendingCombination(null);
    try {
      const outcome = await onCombine((message) => {
        if (attempt.current === run) setNotice({ kind: 'info', text: message });
      });
      if (attempt.current !== run) return;
      setNotice({ kind: outcome.datasets.length ? 'info' : 'warning', text: outcome.note });
      setPendingCombination(outcome.datasets.length ? { ...outcome, basis } : null);
    } catch {
      if (attempt.current !== run) return;
      // A proposed query is not saved until the reader applies it, so a failed
      // attempt leaves every card exactly as it was.
      setNotice({
        kind: 'error',
        text: 'Could not check for shared queries just now. Nothing on this Lens changed. Try again.',
      });
    } finally {
      if (attempt.current === run) setIsCombining(false);
    }
  };

  /** Abandons a run in flight. Its result is dropped rather than shown late. */
  const stopCombining = () => {
    attempt.current += 1;
    setIsCombining(false);
    setNotice({ kind: 'info', text: 'Stopped. Nothing on this Lens changed.' });
  };

  const save = () =>
    onSave(
      datasets.map((dataset) => {
        const edited = draft.find((entry) => entry.id === dataset.id);
        if (!edited || dataset.deletedAt) {
          return dataset;
        }
        const sql = edited.sql.trim();
        // Which date the period follows has to agree with the query as saved:
        // an edit that removes the parameters cannot leave a binding behind
        // claiming to prune on them.
        const dateFilter = reconcileDateFilter(edited.dateFilter, sql);
        const checked = checkedResult(edited);
        const {
          dateFilter: _removed,
          fields: _replaced,
          ...rest
        } = checked ? declaredDataset(dataset, checked) : dataset;
        // Only the fields that can actually be computed against the query as
        // saved: one naming a column an edit has just removed would compute
        // nothing, and a card reading it would render as though the query came
        // back empty.
        const prepared = prepareFields(edited.fields, columnsOf(edited));
        const fields = [...prepared.row, ...prepared.aggregate].map((entry) => entry.field);
        return {
          ...rest,
          maxAgeSeconds: edited.maxAgeSeconds,
          name: edited.name.trim(),
          sql,
          ...(fields.length ? { fields } : {}),
          ...(dateFilter ? { dateFilter } : {}),
        };
      }),
    );

  return (
    <Dialog
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          height: { md: 'min(760px, calc(100dvh - 32px))' },
          maxHeight: 'calc(100dvh - 32px)',
        },
      }}
      fullWidth
      maxWidth="lg"
      onClose={isSaving ? undefined : onClose}
      open={open}
    >
      {historyDataset && lensId && lensVersion && (
        <DatasetHistoryDialog
          dataset={historyDataset}
          lensId={lensId}
          lensVersion={lensVersion}
          readerCount={cards.filter((card) => card.datasetId === historyDataset.id).length}
          onClose={() => setHistoryDataset(null)}
          onRestored={() => {
            setHistoryDataset(null);
            onClose();
          }}
        />
      )}
      <DialogTitle>
        <Stack
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, sm: 2 }}
        >
          <Box>
            <Typography component="div" variant="h6">
              Datasets
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {describeQueryPosture(
                cards.filter((card) => !datasets.find((entry) => entry.id === card.datasetId)?.deletedAt),
              )}
            </Typography>
          </Box>
          {onCombine && combinableSources > 1 && (
            <Stack direction="row" spacing={1}>
              {isCombining && (
                <Button color="inherit" onClick={stopCombining} size="small">
                  Stop
                </Button>
              )}
              <Button
                disabled={isSaving || isCombining}
                onClick={findSharedQueries}
                size="small"
                sx={{ whiteSpace: 'nowrap' }}
                variant="outlined"
              >
                {isCombining ? 'Checking…' : 'Check for shared queries'}
              </Button>
            </Stack>
          )}
        </Stack>
      </DialogTitle>

      {draft.length === 0 ? (
        <DialogContent>
          <Alert severity="info">No queries yet. Add a card and choose its data, or askRichard for a plan.</Alert>
        </DialogContent>
      ) : (
        <>
          <DialogContent
            dividers
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              minHeight: 0,
              overflow: 'hidden',
              p: 0,
            }}
          >
            <Box
              aria-label="Query sources"
              component="nav"
              sx={{
                backgroundColor: 'background.default',
                borderRight: { sm: 1 },
                borderBottom: { xs: 1, sm: 0 },
                borderColor: 'divider',
                flex: { xs: '0 0 auto', sm: '0 0 240px' },
                maxHeight: { xs: 168, sm: 'none' },
                minWidth: 0,
                overflowY: 'auto',
                p: 1,
              }}
            >
              <Typography color="text.secondary" sx={{ px: 1, py: 0.75 }} variant="caption">
                Sources
              </Typography>
              <Stack spacing={0.5}>
                {draft.map((entry, index) => {
                  const readerCount = cards.filter((card) => card.datasetId === entry.id).length;
                  const selected = index === tab;
                  return (
                    <Button
                      aria-current={selected ? 'page' : undefined}
                      key={entry.id}
                      onClick={() => setTab(index)}
                      sx={{
                        alignItems: 'flex-start',
                        backgroundColor: selected ? 'action.selected' : 'transparent',
                        color: 'text.primary',
                        justifyContent: 'flex-start',
                        minHeight: 60,
                        p: 1,
                        textAlign: 'left',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: selected ? 'action.selected' : 'action.hover' },
                      }}
                      variant="text"
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: selected ? 700 : 600 }} variant="body2">
                          {entry.name || entry.id}
                        </Typography>
                        <Typography color="text.secondary" noWrap variant="caption">
                          {`${readerCount} ${readerCount === 1 ? 'card' : 'cards'}`}
                        </Typography>
                      </Box>
                    </Button>
                  );
                })}
              </Stack>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: { xs: 2, md: 3 } }}>
              {notice && (
                <Alert severity={notice.kind} sx={{ mb: 2 }}>
                  {notice.text}
                </Alert>
              )}
              {pendingCombination && (
                <CombineReview
                  onDiscard={() => {
                    setPendingCombination(null);
                    setNotice(null);
                  }}
                  proposal={pendingCombination}
                  stale={staleProposal}
                />
              )}

              {current && (
                <Stack spacing={2.5}>
                  {current.deletedAt && (
                    <Alert
                      severity="warning"
                      action={
                        canEdit ? (
                          <Button disabled={isSaving} onClick={() => onDelete(current.id)}>
                            Restore dataset
                          </Button>
                        ) : undefined
                      }
                    >
                      Dataset deleted. Its linked cards and history are retained.
                    </Alert>
                  )}
                  {canEdit &&
                    lensId &&
                    lensVersion &&
                    datasets.find((entry) => entry.id === current.id)?.scope !== 'shared' && (
                      <Button
                        disabled={Boolean(current.deletedAt) || isSaving}
                        onClick={() => setHistoryDataset(datasets.find((entry) => entry.id === current.id) ?? null)}
                      >
                        Version history
                      </Button>
                    )}
                  <Paper sx={{ backgroundColor: 'background.default', p: 2 }} variant="outlined">
                    <Stack spacing={1.5}>
                      <Stack alignItems="center" direction="row" spacing={1}>
                        <TextField
                          disabled={Boolean(current.deletedAt) || isSaving}
                          fullWidth
                          label="Dataset name"
                          onChange={(event) => update({ name: event.target.value })}
                          size="small"
                          value={current.name}
                        />
                        <Tooltip
                          title={
                            readers.length
                              ? `Delete this dataset and the ${readers.length} card(s) reading it lose their source`
                              : 'Remove this dataset'
                          }
                        >
                          <IconButton
                            aria-label={`Delete ${current.name}`}
                            disabled={Boolean(current.deletedAt) || isSaving}
                            onClick={() => onDelete(current.id)}
                            size="small"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      <Box>
                        <Typography color="text.secondary" sx={{ mb: 0.5 }} variant="caption">
                          Used by
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {readers.length ? (
                            readers.map((card) => (
                              <Chip key={card.id} label={card.title} size="small" variant="outlined" />
                            ))
                          ) : (
                            <Typography color="text.secondary" variant="body2">
                              No cards yet.
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </Paper>

                  <Box>
                    <DatasetPeriod
                      dateFilter={current.dateFilter}
                      disabled={Boolean(current.deletedAt) || isSaving}
                      onChange={(dateFilter) => update({ dateFilter })}
                      sql={current.sql}
                    />

                    <DatasetFieldsEditor
                      columns={columnsOf(current)}
                      disabled={Boolean(current.deletedAt) || isSaving}
                      fields={current.fields ?? []}
                      onChange={(fields) => update({ fields })}
                    />
                  </Box>

                  <TextField
                    disabled={Boolean(current.deletedAt) || isSaving}
                    fullWidth
                    helperText={
                      current.deletedAt
                        ? 'Saved definition. This dataset does not execute while deleted.'
                        : 'Shared source definition. Query execution depends on each card’s grouping and filters; identical reads may be reused.'
                    }
                    label="SQL query"
                    minRows={10}
                    multiline
                    onChange={(event) => update({ sql: event.target.value })}
                    sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 13, lineHeight: 1.55, resize: 'none' } }}
                    value={current.sql}
                  />

                  <Stack alignItems="center" direction="row" spacing={1.5}>
                    <Button
                      disabled={
                        Boolean(current.deletedAt) || isSaving || checkingId === current.id || !current.sql.trim()
                      }
                      onClick={checkCurrentQuery}
                      size="small"
                      sx={{ whiteSpace: 'nowrap' }}
                      variant="outlined"
                    >
                      {checkingId === current.id ? 'Checking…' : 'Check query'}
                    </Button>
                    <Typography color="text.secondary" variant="caption">
                      {describeColumns(current)}
                    </Typography>
                  </Stack>

                  {checkFailure?.id === current.id && checkFailure.sql === current.sql.trim() && (
                    <Alert severity="error">{checkFailure.text}</Alert>
                  )}

                  {readers.length > 1 && (
                    <Alert severity="warning">
                      Changing these columns affects every card above. A card grouping by a column you remove will stop
                      rendering until its cut is changed too.
                    </Alert>
                  )}
                  {readers.length > 0 && (
                    <Alert severity="info">
                      Deleting this dataset keeps these cards and their source references. They stop running until the
                      dataset is restored or a different source is selected.
                    </Alert>
                  )}
                </Stack>
              )}
            </Box>
          </DialogContent>
        </>
      )}

      <DialogActions>
        <Button disabled={isSaving} onClick={onClose}>
          Close
        </Button>
        {pendingCombination && onApplyCombination ? (
          <Button
            disabled={isSaving || isCombining || staleProposal}
            onClick={async () => {
              try {
                await onApplyCombination(pendingCombination);
                setPendingCombination(null);
                setNotice(null);
                setSeed((count) => count + 1);
              } catch (error) {
                // A save is rejected outright when the dashboard has moved on,
                // which is what a second tab or another editor looks like. The
                // dashboard on screen has just been reloaded underneath, so the
                // proposal describes a board that no longer exists and applying
                // it would undo whatever that other change was. Every other
                // failure changed nothing, so the proposal stays available and
                // the same button retries it.
                const conflict = (error as { status?: number } | null)?.status === 409;
                if (conflict) {
                  setPendingCombination(null);
                }
                setNotice({
                  kind: conflict ? 'warning' : 'error',
                  text: conflict
                    ? 'This Lens was changed somewhere else while you were reviewing, so nothing here was saved. The current version is now on screen. Check for shared queries again to review it against this version.'
                    : 'Could not save this change just now. Nothing on this Lens changed, and this proposal is still here. Try again.',
                });
              }
            }}
            variant="contained"
          >
            Apply
          </Button>
        ) : null}
        {draft.length > 0 && (
          <Button
            disabled={isSaving || draft.some((entry) => !entry.name.trim() || !entry.sql.trim() || isUnchecked(entry))}
            onClick={save}
            variant="contained"
          >
            {isSaving ? 'Saving…' : 'Save datasets'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * The review a reader says yes or no to.
 *
 * Three questions have to be answerable without knowing what a dataset is:
 * what does this change, what does it cost, and what happens if I am wrong.
 * So it names the queries before and after, lists every card that moves and
 * every card that does not with the reason it did not, and says plainly that
 * nothing happens until Apply and that a failed Apply changes nothing.
 */
function CombineReview({ proposal, stale, onDiscard }: { proposal: Proposal; stale: boolean; onDiscard: () => void }) {
  const { queriesBefore, queriesAfter } = proposal.cost;
  const moves = proposal.decisions.filter((entry) => entry.kind === 'moves');
  const keeps = proposal.decisions.filter((entry) => entry.kind === 'keeps');
  const saved = queriesBefore - queriesAfter;
  const byDataset = groupMoves(moves);
  const byReason = groupKeeps(keeps);

  return (
    <Paper sx={{ backgroundColor: 'background.default', mb: 2, p: 2 }} variant="outlined">
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle1">
            {queriesBefore} {queriesBefore === 1 ? 'query' : 'queries'} now, {queriesAfter} after this change
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Every query is its own trip to the warehouse, and the Lens is not finished until the slowest one is.
            {saved > 0
              ? ` Sharing ${saved === 1 ? 'one' : saved} of them means less data read and less waiting each time anyone opens this Lens.`
              : ' This proposal does not reduce that count, so there is nothing to gain by applying it.'}
          </Typography>
        </Box>

        {byDataset.map((group) => (
          <Box key={group.datasetId}>
            <Typography variant="subtitle2">
              {group.cards.length} {group.cards.length === 1 ? 'card reads' : 'cards read'} one shared query
            </Typography>
            <Typography color="text.secondary" sx={{ display: 'block', mb: 0.5 }} variant="caption">
              {group.datasetName}. BigQuery verified that the proposed query reads the same source tables. Review its
              filters and measures before applying it; nothing changes until you do.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {group.cards.map((card) => (
                <Chip key={card.cardId} label={card.title} size="small" variant="outlined" />
              ))}
            </Stack>
          </Box>
        ))}

        {byReason.map((group) => (
          <Box key={group.reason}>
            <Typography variant="subtitle2">
              {group.cards.length === 1
                ? '1 card keeps its own query'
                : `${group.cards.length} cards keep their own queries`}
            </Typography>
            <Typography color="text.secondary" sx={{ display: 'block', mb: 0.5 }} variant="caption">
              {describeKeptReason(group.reason)}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {group.cards.map((card) => (
                <Chip key={card.cardId} label={card.title} size="small" variant="outlined" />
              ))}
            </Stack>
          </Box>
        ))}

        {stale ? (
          <Alert
            action={
              <Button color="inherit" onClick={onDiscard} size="small">
                Discard
              </Button>
            }
            severity="warning"
          >
            This Lens has changed since this was prepared, so it cannot be applied without undoing that change. Discard
            it and check again.
          </Alert>
        ) : (
          <Typography color="text.secondary" variant="caption">
            Nothing has changed yet. Apply saves the Lens once: the cards above start reading the shared
            {moves.length === 1 ? ' query' : ' queries'} and the copies they carried are removed. If the save does not
            go through, nothing changes and this stays here to try again.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

/** Moving cards grouped by the query they would read, since that is the unit
 *  a reader is being asked to accept. */
function groupMoves(
  moves: Array<CombineDecision>,
): Array<{ datasetId: string; datasetName: string; cards: Array<{ cardId: string; title: string }> }> {
  const groups = new Map<
    string,
    { datasetId: string; datasetName: string; cards: Array<{ cardId: string; title: string }> }
  >();
  for (const entry of moves) {
    if (entry.kind !== 'moves') continue;
    const group = groups.get(entry.datasetId) ?? {
      datasetId: entry.datasetId,
      datasetName: entry.datasetName,
      cards: [],
    };
    group.cards.push({ cardId: entry.cardId, title: entry.title });
    groups.set(entry.datasetId, group);
  }
  return [...groups.values()];
}

/** Kept cards grouped by reason, so one sentence covers all of them rather
 *  than repeating per card. */
function groupKeeps(
  keeps: Array<CombineDecision>,
): Array<{ reason: KeptReason; cards: Array<{ cardId: string; title: string }> }> {
  const groups = new Map<KeptReason, Array<{ cardId: string; title: string }>>();
  for (const entry of keeps) {
    if (entry.kind !== 'keeps') continue;
    groups.set(entry.reason, [...(groups.get(entry.reason) ?? []), { cardId: entry.cardId, title: entry.title }]);
  }
  return [...groups.entries()].map(([reason, cards]) => ({ reason, cards }));
}

/** Both halves of the dashboard period, bound by the query itself. */
function bindsPeriod(sql: string): boolean {
  return /@start_date\b/.test(sql) && /@end_date\b/.test(sql);
}

/**
 * Keep the stored binding truthful about the query as saved.
 *
 * Both fields are claims about the SQL, and the SQL is editable here. Two ways
 * they can be made to lie:
 *
 * `pruned` says the query applies the range itself, so deleting the parameters
 * has to clear it or the period would appear to work and return the same rows.
 *
 * `column` says which date the range follows, and for a parameterised query
 * that is decided by its predicate, not by this dialog. Letting the two differ
 * would filter on both dates at once — an intersection nobody asked for.
 */
export function reconcileDateFilter(
  dateFilter: LensDatasetDateFilter | undefined,
  sql: string,
): LensDatasetDateFilter | undefined {
  if (!dateFilter) {
    return undefined;
  }
  const pruned = bindsPeriod(sql);
  const inSql = pruned ? dateColumnFromBinding(sql) : undefined;
  return { ...dateFilter, pruned, ...(inSql ? { column: inSql } : {}) };
}

/**
 * Which date the dashboard period follows for this dataset.
 *
 * A parameterised query decides this itself: the predicate sits next to the
 * partitioning column, which is what lets BigQuery skip the periods nobody
 * asked for. Following a different date there is a change to the query, not to
 * a setting — pointing a second date at a query already bound to the first
 * would return the rows in both periods rather than the one chosen.
 *
 * A query with no date bound of its own is the opposite case: the range is
 * applied around its result, so any date column it returns is fair game.
 */
function DatasetPeriod({
  dateFilter,
  sql,
  onChange,
  disabled,
}: {
  dateFilter?: LensDatasetDateFilter;
  sql: string;
  onChange: (dateFilter: LensDatasetDateFilter | undefined) => void;
  disabled?: boolean;
}) {
  const parameterised = bindsPeriod(sql);
  const boundColumn = parameterised ? dateColumnFromBinding(sql) : undefined;

  if (!dateFilter) {
    return (
      <Alert severity="info" sx={{ mt: 2 }} variant="outlined">
        The Lens period does not move this dataset.{' '}
        {parameterised
          ? `Its query is bound to ${boundColumn ?? 'a date'} through @start_date and @end_date. Connect it below.`
          : 'Either add WHERE DATE(<column>) >= @start_date AND DATE(<column>) < @end_date to its query, which is the cheap form, or connect it to a date it already returns.'}
        {'  '}
        <Button
          disabled={disabled}
          onClick={() => onChange({ column: boundColumn ?? '', pruned: parameterised })}
          size="small"
        >
          Connect
        </Button>
      </Alert>
    );
  }

  const column = boundColumn ?? dateFilter.column;
  const alternatives = [...new Set([column, ...(dateFilter.available ?? [])])].filter(Boolean);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography color="text.secondary" sx={{ mb: 0.5 }} variant="caption">
        Period follows
      </Typography>
      <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
        {parameterised ? (
          <Chip color="success" label={column || 'a date in the query'} size="small" />
        ) : (
          <TextField
            disabled={disabled}
            helperText="Any date column this query returns."
            onChange={(event) => onChange({ ...dateFilter, column: event.target.value })}
            select={alternatives.length > 1}
            size="small"
            SelectProps={alternatives.length > 1 ? { native: true } : undefined}
            sx={{ minWidth: 220 }}
            value={dateFilter.column}
          >
            {alternatives.length > 1 &&
              alternatives.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
          </TextField>
        )}
        <Chip
          color={parameterised ? 'success' : 'default'}
          label={parameterised ? 'Prunes partitions' : 'Filters after the query'}
          size="small"
          variant="outlined"
        />
        <Button color="inherit" disabled={disabled} onClick={() => onChange(undefined)} size="small">
          Disconnect
        </Button>
      </Stack>

      <Typography color="text.secondary" sx={{ display: 'block', mt: 0.5 }} variant="caption">
        {parameterised ? (
          <>
            To use a different date, update the date condition in the SQL query below
            {alternatives.length > 1
              ? ` — this one also returns ${alternatives.filter((entry) => entry !== column).join(', ')}.`
              : '.'}
          </>
        ) : (
          <>
            Applied around the finished query, so it returns the right rows but reads the whole result first. Binding
            @start_date and @end_date inside the query instead is the cheap form.
          </>
        )}
      </Typography>
    </Box>
  );
}
